/**
 * Working through the pending queue one résumé at a time.
 *
 * A module rather than a closure in the page so the two properties that matter can actually be
 * tested: that it runs strictly one at a time, and that one bad document does not abandon the rest
 * of the queue.
 */

export type BulkOutcome = 'created' | 'no-seeker' | 'failed';

export type BulkProgress = {
    done: number;
    total: number;
    created: number;
    failed: number;
};

export type BulkResult = BulkProgress & { stopped: boolean };

/**
 * Run `processOne` over every id, in order, waiting for each.
 *
 * **Serial on purpose, and this is the load-bearing decision.** The mutation behind `processOne`
 * does an S3 download, a PDFBox parse and an untimed OpenAI call on a Netty event-loop thread, so
 * firing a queue of them at once would occupy the whole event-loop pool and stall the service for
 * every other request, not just this page. A `Promise.all` here would be a site-wide outage rather
 * than a faster import.
 *
 * A row that throws is counted and skipped. The usual failures are properties of one document, a
 * scan with no text layer or a CV with no email address, and stopping the run on the first would
 * leave the remaining queue untouched for no reason. The proxy's 30s limit is the same story: it
 * gives up while the backend carries on, so the row may still land, and the hourly sweeper reclaims
 * it if it does not.
 *
 * `shouldStop` is read between rows, never mid-row, so the in-flight résumé always finishes. There
 * is no way to abandon it that would not leave the row stranded in PROCESSING.
 */
export async function runBulk(
    ids: string[],
    processOne: (id: string) => Promise<BulkOutcome>,
    opts: {
        onProgress?: (progress: BulkProgress) => void;
        shouldStop?: () => boolean;
        onError?: (id: string, error: unknown) => void;
    } = {},
): Promise<BulkResult> {
    const { onProgress, shouldStop, onError } = opts;
    let created = 0;
    let failed = 0;
    let done = 0;

    for (const id of ids) {
        if (shouldStop?.()) {
            return { done, total: ids.length, created, failed, stopped: true };
        }
        try {
            const outcome = await processOne(id);
            if (outcome === 'created') created += 1;
            else if (outcome === 'failed') failed += 1;
        } catch (error) {
            onError?.(id, error);
            failed += 1;
        }
        done += 1;
        onProgress?.({ done, total: ids.length, created, failed });
    }

    return { done, total: ids.length, created, failed, stopped: false };
}
